import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute from "../ProtectedRoute/ProtectedRoute";
import ManagerEntitlementRoute from "../ProtectedRoute/ManagerEntitlementRoute";
import PlaylistDetails from "@src/pages/PlaylistDetails";
import Playlists from "@src/pages/Playlists";
import GuestCurator from "@src/pages/GuestCurator";
import ScheduleCalendar from "@src/pages/ScheduleCalendar";
import SocialMediaPromo from "@src/pages/SocialMediaPromo";
import Tracks from "@src/pages/Tracks";
import Login from "@src/pages/Login";
import MessagesPage from "@src/pages/Messages/MessagesPage";
import { AnimatePresence } from "framer-motion";
import ResetPassword from "@src/pages/ResetPassword";
import TagFactory from "@src/pages/TagFactory";
import ResetPasswordForm from "@src/pages/ResetPassword/ResetPasswordForm";
import EmailConfirmed from "@src/pages/EmailConfirmed";
import { MaintenancePage } from "@src/pages/MaintenancePage/MaintenancePage";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { logout } from "@src/redux/slices/authSlice";
import { MaintenanceLoginPage } from "@src/pages/MaintenanceLoginPage/MaintenanceLoginPage";
import DownloadDesktopApp from "@src/pages/DownloadDesktopApp/DownloadDesktopApp";
import VinylPage from "@src/pages/VinylPage/VinylPage";
import ArticlesListPage from "@src/pages/ArticlesList/ArticlesListPage";
import WriteArticlePage from "@src/pages/WriteArticle/WriteArticlePage";
import CuratorManagement from "@src/pages/CuratorManagement";
import ClientsExportPage from "@src/pages/ClientsExport/ClientsExportPage";
import ScheduleBoard from "@src/pages/ScheduleBoard";
import InvoicesPage from "@src/pages/Invoices";
import StatusCheckPage from "@src/pages/StatusCheck/StatusCheckPage";
import AdminMessagesPage from "@src/pages/AdminMessages/AdminMessagesPage";
import { FEATURES } from "@src/constants";
import { MANAGER_ENTITLEMENTS } from "@src/types/managerConfiguration";

const AnimatedRoutes = (): React.JSX.Element => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isBypassed } = useAppSelector((state) => state.maintenanceSession);
  useEffect(() => {
    if (process.env.REACT_APP_MAINTENANCE_MODE === "true" && !isBypassed) {
      dispatch(logout());
    }
  }, [location]);
  const SafeAnimatePresence = AnimatePresence as React.ComponentType<
    React.PropsWithChildren<{ mode?: "wait" | "sync" | "popLayout" }>
  >;

  if (process.env.REACT_APP_MAINTENANCE_MODE === "true" && !isBypassed) {
    return (
      <SafeAnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<MaintenancePage />} />
          <Route path="/desktop" element={<DownloadDesktopApp />} />
          <Route
            path="/maintenance-password"
            element={<MaintenanceLoginPage />}
          />
          <Route path="*" element={<Navigate to={"/"} />} />
        </Routes>
      </SafeAnimatePresence>
    );
  }
  return (
    <SafeAnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/vinyl-list"
          element={
            <ProtectedRoute>
              <VinylPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/playlist/:playlistId"
          element={
            <ProtectedRoute>
              <PlaylistDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/playlists"
          element={
            <ProtectedRoute>
              <Playlists />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-curator"
          element={
            <ProtectedRoute>
              <GuestCurator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/library"
          element={
            <ProtectedRoute>
              <Tracks />
            </ProtectedRoute>
          }
        />
        {FEATURES.MESSAGING_ENABLED && (
          <Route
            path="/messages"
            element={
              <ProtectedRoute curatorOnlyRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Playlists />
            </ProtectedRoute>
          }
        />
        {FEATURES.ARTICLES_ENABLED && (
          <Route
            path="/articles"
            element={
              <ProtectedRoute articleRoute>
                <ArticlesListPage />
              </ProtectedRoute>
            }
          />
        )}
        {FEATURES.ARTICLES_ENABLED && (
          <Route
            path="/write-article"
            element={
              <ProtectedRoute articleRoute>
                <WriteArticlePage />
              </ProtectedRoute>
            }
          />
        )}
        {FEATURES.ARTICLES_ENABLED && (
          <Route
            path="/write-article/:articleId"
            element={
              <ProtectedRoute articleRoute>
                <WriteArticlePage />
              </ProtectedRoute>
            }
          />
        )}
        {/* admin routes */}
        <Route
          path="/schedule-calendar"
          element={
            <ProtectedRoute>
              <ScheduleCalendar />
            </ProtectedRoute>
          }
        />
        {FEATURES.INVOICES_ENABLED && (
          <Route
            path="/invoices"
            element={
              <ProtectedRoute noManagerRoute>
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
        )}

        <Route
          path="/tag-factory"
          element={
            <ProtectedRoute adminRoute>
              <TagFactory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mood-factory"
          element={
            <ProtectedRoute adminRoute>
              <Playlists isMoodFactory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mood-factory/:moodFactoryId/playlist/:playlistId"
          element={
            <ProtectedRoute>
              <PlaylistDetails isMoodFactory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/curator-management"
          element={
            <ProtectedRoute adminRoute>
              <CuratorManagement />
            </ProtectedRoute>
          }
        />
        {FEATURES.CLIENTS_EXPORT_ENABLED && (
          <Route
            path="/clients-export"
            element={
              <ProtectedRoute adminOnlyRoute>
                <ClientsExportPage />
              </ProtectedRoute>
            }
          />
        )}
        {FEATURES.SCHEDULE_BOARD_ENABLED && (
          <Route
            path="/schedule-board"
            element={
              <ProtectedRoute adminOnlyRoute>
                <ScheduleBoard />
              </ProtectedRoute>
            }
          />
        )}
        {FEATURES.ADMIN_CURATOR_MESSAGES_ENABLED && (
          <Route
            path="/admin-messages"
            element={
              <ProtectedRoute adminRoute>
                <ManagerEntitlementRoute
                  entitlement={MANAGER_ENTITLEMENTS.ADMIN_MESSAGES}
                >
                  <AdminMessagesPage />
                </ManagerEntitlementRoute>
              </ProtectedRoute>
            }
          />
        )}
        {FEATURES.STATUS_CHECK_ENABLED && (
          <Route
            path="/status-check"
            element={
              <ProtectedRoute adminOnlyRoute>
                <StatusCheckPage />
              </ProtectedRoute>
            }
          />
        )}
        {(FEATURES.SOCIAL_MEDIA_PROMO_ADMIN_ENABLED ||
          FEATURES.SOCIAL_MEDIA_PROMO_CURATOR_ENABLED) && (
          <Route
            path="/social-media-promo"
            element={
              /* One flag per audience [ROVR-3406]: `adminRoute` keeps curators out
                 while their flag is off, `curatorOnlyRoute` keeps admins and
                 managers out while theirs is. With both flags on neither applies
                 and every dashboard role reaches the page. */
              <ProtectedRoute
                adminRoute={!FEATURES.SOCIAL_MEDIA_PROMO_CURATOR_ENABLED}
                curatorOnlyRoute={!FEATURES.SOCIAL_MEDIA_PROMO_ADMIN_ENABLED}
              >
                <SocialMediaPromo />
              </ProtectedRoute>
            }
          />
        )}
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ResetPassword />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="/email-confirmed" element={<EmailConfirmed />} />
        <Route path="/desktop" element={<DownloadDesktopApp />} />
        <Route path="*" element={<Navigate to={"/"} />} />
      </Routes>
    </SafeAnimatePresence>
  );
};

export default AnimatedRoutes;
